import React, { useContext, useState } from "react";
import {
  IonButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonHeader,
  IonItem,
  IonLabel,
  IonModal,
  IonNote,
  IonTextarea,
  IonTitle,
  IonToolbar,
  useIonToast,
} from "@ionic/react";
import { TranslationsContext } from "../util/Translations";
import { copyWorkOrderToClipboard, shareWorkOrder } from "../util/ShareHelper";

interface ShareWorkOrderModalProps {
  isOpen: boolean;
  woNumber: string;
  description?: string;
  onClose: () => void;
}

/**
 * Lets the user add a comment and share a link to the current work order with a
 * colleague via the native share sheet (text / email / Teams / …).
 */
const ShareWorkOrderModal: React.FC<ShareWorkOrderModalProps> = ({
  isOpen,
  woNumber,
  description,
  onClose,
}) => {
  const { translations } = useContext(TranslationsContext);
  const [presentToast] = useIonToast();
  const [comment, setComment] = useState("");
  const [sharing, setSharing] = useState(false);

  const woWord = translations["lbl_work_order"] || "Work Order";

  const shareInput = () => ({
    woNumber,
    description,
    comment,
    labels: {
      title: `${woWord} ${woNumber}`,
      dialogTitle: translations["lbl_share_work_order"] || "Share work order",
      workOrderWord: woWord,
    },
  });

  const handleShare = async () => {
    setSharing(true);
    try {
      await shareWorkOrder(shareInput());
    } finally {
      setSharing(false);
      setComment("");
      onClose();
    }
  };

  const handleCopy = async () => {
    const ok = await copyWorkOrderToClipboard(shareInput());
    presentToast({
      message: ok
        ? translations["lbl_copied_to_clipboard"] || "Copied to clipboard"
        : translations["lbl_copy_failed"] || "Could not copy",
      duration: 1500,
      position: "bottom",
    });
  };

  const handleClose = () => {
    setComment("");
    onClose();
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={handleClose}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>
            {translations["lbl_share_work_order"] || "Share work order"}
          </IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={handleClose}>
              {translations["lbl_btn_cancel"] || "Cancel"}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent class="ion-padding">
        <IonItem lines="none">
          <IonLabel>
            {woWord} {woNumber}
          </IonLabel>
        </IonItem>
        <IonItem>
          <IonLabel position="stacked">
            {translations["lbl_comment"] || "Comment"}
          </IonLabel>
          <IonTextarea
            value={comment}
            autoGrow
            rows={4}
            placeholder={
              translations["lbl_share_comment_placeholder"] ||
              "Add a comment for your colleague (optional)"
            }
            onIonChange={(e) => setComment(e.detail.value ?? "")}
          />
        </IonItem>
        <IonNote class="ion-padding-start">
          {translations["lbl_share_help"] ||
            "A link to this work order will be shared along with your comment."}
        </IonNote>
      </IonContent>
      <IonFooter>
        <IonButton expand="block" disabled={sharing} onClick={handleShare}>
          {translations["lbl_btn_share"] || "Share"}
        </IonButton>
        <IonButton
          expand="block"
          fill="outline"
          disabled={sharing}
          onClick={handleCopy}
        >
          {translations["lbl_btn_copy"] || "Copy"}
        </IonButton>
      </IonFooter>
    </IonModal>
  );
};

export default ShareWorkOrderModal;
